import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, XCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { verifyTransaction } from '../services/paymentService';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackPurchase } from '../utils/analytics';
import { trackEventOnce } from '../utils/visitorTracking';
import { triggerInvoiceReceiptEmail } from '../services/emailTriggerService';
export function PaymentCallbackPage() {
  const {
    user,
    refreshUser
  } = useAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'pending' | 'failed'>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 30; // 30 retries * 2 seconds = 1 minute max
  useEffect(() => {
    document.title = 'Payment Verification - Toxic Detector';
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference') || urlParams.get('trxref');
    if (!reference) {
      setError('No payment reference found in URL');
      setVerificationStatus('failed');
      return;
    }
    if (!user?.id) {
      setError('User not authenticated');
      setVerificationStatus('failed');
      return;
    }
    verifyPayment(reference);
  }, [user?.id]);
  const verifyPayment = async (reference: string) => {
    try {
      console.log('Verifying transaction with reference:', reference);
      const response = await verifyTransaction(reference);
      console.log('Verification response:', response);
      if (response.status === 'success') {
        // Payment successful
        setVerificationStatus('success');
        // Track payment completion
        await trackCriticalEvent('Payment_Completed', {
          plan_type: response.plan_type || 'unknown',
          amount_paid: response.amount || 0,
          transaction_reference: reference,
          payment_method: 'paystack',
          is_first_purchase: response.is_first_purchase || false
        });
        // GA4: only after Paystack actually confirmed success. Deduped by
        // transaction reference so a refresh cannot double-count revenue.
        trackPurchase(reference, response.plan_type || 'unknown');
        // First-party: keyed on the transaction reference so a refresh or a
        // retry loop can never record the same purchase twice.
        void trackEventOnce(`purchase_completed:${reference}`, 'purchase_completed', {
          reference,
          plan_type: response.plan_type || 'unknown',
          amount: response.amount ?? null
        });
        // Queue invoiceReceipt email via email_logs table
        try {
          const amountStr = response.amount ? `${(response.amount / 100).toFixed(2)}` : '$2.99';
          await triggerInvoiceReceiptEmail(`${window.location.origin}/payment-success`, `${window.location.origin}/dashboard`, amountStr, {
            invoiceNumber: reference,
            planName: response.plan_type || 'Single Scan'
          });
        } catch (emailErr) {
          console.error('[PaymentCallback] Failed to queue invoice email:', emailErr);
          // Non-blocking — don't fail the payment flow for an email issue
        }
        // Update local state
        await refreshUser();
        // Redirect to success page after brief delay
        setTimeout(() => {
          navigate('/payment-success');
        }, 1000);
      } else if (response.status === 'pending') {
        // Payment still pending
        setVerificationStatus('pending');
        // Retry after 2 seconds if under max retries
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            setVerificationStatus('verifying');
            verifyPayment(reference);
          }, 2000);
        } else {
          setError('Payment verification timed out. Please contact support if you were charged.');
          setVerificationStatus('failed');
          // Track timeout failure
          await trackCriticalEvent('Payment_Failed', {
            plan_type: 'unknown',
            failure_reason: 'verification_timeout',
            transaction_reference: reference
          });
        }
      } else {
        // Payment failed
        setError(response.message || 'Payment verification failed');
        setVerificationStatus('failed');
        // Track payment failure
        await trackCriticalEvent('Payment_Failed', {
          plan_type: response.plan_type || 'unknown',
          failure_reason: response.message || 'verification_failed',
          transaction_reference: reference
        });
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to verify payment');
      setVerificationStatus('failed');
      // Track error
      await trackCriticalEvent('Payment_Failed', {
        plan_type: 'unknown',
        failure_reason: err.message || 'exception',
        transaction_reference: reference
      });
    }
  };
  // Verifying state
  if (verificationStatus === 'verifying') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9173" data-mp="1wth9yzg2jga9">
        <WebsiteHeader data-id="element-9174" data-mp="1itg1axtkb4k8" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9175" data-mp="1iu9a04c30450">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-[#FF0000]/30 shadow-[0_0_50px_rgba(255,0,0,0.1)]" data-id="element-9176" data-mp="1nsj5yh5mr8ro">
            <div className="text-center" data-id="element-9177" data-mp="7e13jrtj48o2">
              <div className="mb-8 flex justify-center" data-id="element-9178" data-mp="ce5i3cavmtbn">
                <div className="relative" data-id="element-9179" data-mp="cfqcn1x6yc9i">
                  <div className="absolute inset-0 border-4 border-[#FF0000]/20 rounded-full animate-ping" data-id="element-9180" data-mp="3oi8a7hq10kte"></div>
                  <div className="relative w-24 h-24 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin" data-id="element-9181" data-mp="3kfv58vj3sqxo"></div>
                  <Loader2 size={48} className="absolute inset-0 m-auto text-[#FF0000] animate-pulse" data-id="element-9182" data-mp="3fnk5pts9z75n" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9183" data-mp="35siij3hk8f5p">
                Verifying Transaction
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9184" data-mp="2wpr4qx17p7aw">
                Confirming your payment with Paystack...
              </p>

              <div className="bg-[#FF0000]/5 border border-[#FF0000]/20 rounded-xl p-4" data-id="element-9185" data-mp="2rtispsib9u7y">
                <p className="text-[#FF0000] font-mono text-xs uppercase tracking-wider" data-id="element-9186" data-mp="uyead1wveht3">
                  ⚡ Processing • Do not close this window
                </p>
              </div>

              {/* Animated dots */}
              <div className="flex items-center justify-center space-x-2 mt-8" data-id="element-9187" data-mp="xhrcmn1du8yd">
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-9188" data-mp="12glw7r76c1jj"></div>
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-9189" data-mp="179x31sbzkibg"></div>
                <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-9190" data-mp="emubjk5q97up"></div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Pending state (waiting for payment to complete)
  if (verificationStatus === 'pending') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9191" data-mp="200f6iugmglpe">
        <WebsiteHeader data-id="element-9192" data-mp="23y36ibx694o5" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9193" data-mp="241imri2l0jr5">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-[#FFD700]/30 shadow-[0_0_50px_rgba(255,215,0,0.1)]" data-id="element-9194" data-mp="2q1pvnewwu52q">
            <div className="text-center" data-id="element-9195" data-mp="2l0a91if33qi8">
              <div className="mb-8 flex justify-center" data-id="element-9196" data-mp="2zr9dqicstuwj">
                <div className="bg-[#FFD700]/10 rounded-full p-6 border border-[#FFD700]/30" data-id="element-9197" data-mp="2zsucyla2620q">
                  <Loader2 size={64} className="text-[#FFD700] animate-spin" data-id="element-9198" data-mp="2uygbq28pt0o5" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9199" data-mp="33suz6s3efhic">
                Payment Pending
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9200" data-mp="3ip3x9qbjzot6">
                Your payment is being processed. This usually takes a few
                seconds...
              </p>

              <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-xl p-4 mb-6" data-id="element-9201" data-mp="1ocnl68w2tgge">
                <p className="text-[#FFD700] font-mono text-xs uppercase tracking-wider mb-2" data-id="element-9202" data-mp="1je2rksbbxv0x">
                  Retry {retryCount + 1} of {MAX_RETRIES}
                </p>
                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden" data-id="element-9203" data-mp="3ubowklnsok7">
                  <div className="bg-[#FFD700] h-full transition-all duration-300" style={{
                  width: `${(retryCount + 1) / MAX_RETRIES * 100}%`
                }} data-id="element-9204" data-mp="3lal1hnuem4w3"></div>
                </div>
              </div>

              <p className="text-gray-500 font-mono text-xs" data-id="element-9205" data-mp="3gayrrodsb6s1">
                Checking payment status every 2 seconds...
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  // Failed state
  if (verificationStatus === 'failed') {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9206" data-mp="37cdcc23m07vh">
        <WebsiteHeader data-id="element-9207" data-mp="10klx1ockidy8" />

        <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9208" data-mp="10i9izm9xknqg">
          <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-red-500/30 shadow-[0_0_50px_rgba(239,68,68,0.1)]" data-id="element-9209" data-mp="15ingiadwofa8">
            <div className="text-center" data-id="element-9210" data-mp="1nilupccw2gl8">
              <div className="mb-8 flex justify-center" data-id="element-9211" data-mp="1sbeyny0j9kw9">
                <div className="bg-red-500/10 rounded-full p-6 border border-red-500/30" data-id="element-9212" data-mp="1sczwbwi59jyc">
                  {error?.includes('timed out') ? <AlertTriangle size={64} className="text-yellow-500" data-id="element-9213" data-mp="1jjwl7uuz1j6v" /> : <XCircle size={64} className="text-red-500" data-id="element-9214" data-mp="279h5ch2v2mhq" />}
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9215" data-mp="2b9hkk3xsb1pu">
                {error?.includes('timed out') ? 'Verification Timeout' : 'Verification Failed'}
              </h1>

              <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-9216" data-mp="lujpz1ae4gwo">
                {error || 'Unable to verify your payment'}
              </p>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-8 text-left" data-id="element-9217" data-mp="qvh85dvehay6">
                <p className="text-blue-400 font-mono text-xs uppercase tracking-wider mb-3" data-id="element-9218" data-mp="j8twece8d31o">
                  What to do next:
                </p>
                <ul className="text-gray-300 text-sm space-y-2 font-mono" data-id="element-9219" data-mp="sbuzs5bpog1n">
                  <li data-id="element-9220" data-mp="nc8oi1fg19vh">• Check your email for payment confirmation</li>
                  <li data-id="element-9221" data-mp="nd1liaolol0f">• Refresh this page in a few minutes</li>
                  <li data-id="element-9222" data-mp="3vraal8rwytnu">• Contact support if payment was deducted</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-9223" data-mp="3rrhv0c43rn8z">
                <button onClick={() => window.location.reload()} className="flex-1 bg-[#FF0000] text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-red-600 transition-colors" data-id="element-9224" data-mp="9c7bjnlwmbzn">
                  Refresh Page
                </button>
                <Link to="/dashboard" className="flex-1 bg-white/10 border border-gray-700 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-white/20 transition-colors text-center" data-id="element-9225" data-mp="3830rwfpzym0x">
                  Go to Dashboard
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-800" data-id="element-9226" data-mp="1lpnyk4a73h04">
                <p className="text-gray-500 font-mono text-xs" data-id="element-9227" data-mp="1qoqvf1g0ugx0">
                  Need help?{' '}
                  <a href="mailto:support@toxicdetector.com" className="text-[#FF0000] hover:underline" data-id="element-9228" data-mp="t10bc35vyhjx">
                    Contact support
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Success state (brief, before redirect)
  return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9229" data-mp="2edthclhouvxb">
      <WebsiteHeader data-id="element-9230" data-mp="2efw7r6mr6r2k" />

      <div className="max-w-2xl mx-auto w-full px-5 pt-32 pb-10" data-id="element-9231" data-mp="2ifeqbvr3qq28">
        <div className="bg-[#0a0a0a] rounded-2xl p-8 md:p-12 border border-green-500/30 shadow-[0_0_50px_rgba(34,197,94,0.1)]" data-id="element-9232" data-mp="2idlkujs86bvt">
          <div className="text-center" data-id="element-9233" data-mp="2qg9hoc09y0v">
            <div className="mb-8 flex justify-center" data-id="element-9234" data-mp="2rj1jzl2e6id">
              <div className="bg-green-500/10 rounded-full p-6 border border-green-500/30 animate-pulse" data-id="element-9235" data-mp="3u55kzgq0rrwt">
                <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-9236" data-mp="3p6ar9jqikixx">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-9237" data-mp="3f84oz10spasj" />
                </svg>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-9238" data-mp="36915hvxputdi">
              Payment Verified
            </h1>

            <p className="text-gray-400 font-mono text-sm" data-id="element-9239" data-mp="14fa7h3kmpjcj">
              Redirecting to success page...
            </p>
          </div>
        </div>
      </div>
    </div>;
}